import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaDumbbell, FaFire } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Widget que muestra el progreso del programa Fit Evolution de 12 semanas
 * en el dashboard de la clienta. Se muestra solo para users basic con plan
 * publicado.
 */
export default function FitEvolutionWorkoutProgressWidget({ user }) {
  const navigate = useNavigate();
  const [prog, setProg] = useState(null);

  useEffect(() => {
    if (!user?.email || user?.subscription_plan !== 'basic') return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/workout-progression/${encodeURIComponent(user.email)}`);
        if (res.data?.active) setProg(res.data);
      } catch (e) { /* silent */ }
    })();
  }, [user]);

  if (!prog?.active) return null;

  const { current_week, current_day, block, message, level, cycle_number, week_in_cycle } = prog;
  // Barra de progreso: relativa al ciclo actual de 12 semanas.
  const pct = Math.round(((week_in_cycle || current_week) / 12) * 100);
  // Mostrar el mensaje de "Bienvenida al bloque" sólo durante los primeros 3
  // días de la semana en que arranca el bloque (evita que el mensaje se vea
  // 7 días seguidos y se perciba como un "reset").
  const showBlockWelcome = block?.is_block_start && (typeof current_day !== 'number' || current_day <= 3);

  // Color del badge de nivel según key.
  const levelStyles = {
    novato: { bg: 'linear-gradient(135deg, rgba(148,163,184,0.20), rgba(148,163,184,0.10))', border: 'rgba(148,163,184,0.45)', text: '#e2e8f0' },
    intermedio: { bg: 'linear-gradient(135deg, rgba(236,72,153,0.22), rgba(236,72,153,0.10))', border: 'rgba(236,72,153,0.45)', text: '#fbcfe8' },
    avanzado: { bg: 'linear-gradient(135deg, rgba(250,204,21,0.25), rgba(249,115,22,0.15))', border: 'rgba(250,204,21,0.55)', text: '#fef08a' },
  };
  const lvStyle = levelStyles[level?.key] || levelStyles.novato;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 md:p-5 mb-4"
      style={{
        background: 'linear-gradient(135deg, rgba(0,240,255,0.10), rgba(var(--brand-primary-glow),0.05))',
        border: '1.5px solid rgba(0,240,255,0.35)'
      }}
      data-testid="fit-evo-workout-widget"
    >
      <div className="flex items-start gap-4">
        <div className="text-3xl md:text-4xl leading-none pt-1"><FaDumbbell style={{ color: '#00F0FF' }} /></div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-cyan-300/90 font-bold mb-1">
            Fit Evolution · Programa continuo
          </p>
          <h3 className="text-base md:text-lg font-black text-white mb-2 flex flex-wrap items-baseline gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <span data-testid="fit-evo-current-week">Semana {current_week}</span>
            {typeof current_day === 'number' && current_day > 0 && (
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,240,255,0.25), rgba(var(--brand-primary-glow),0.20))',
                  border: '1px solid rgba(0,240,255,0.45)',
                  color: '#a5f3fc',
                }}
                data-testid="current-day-badge"
              >
                Día {current_day}
              </span>
            )}
            {typeof cycle_number === 'number' && cycle_number > 0 && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider text-white/80"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)' }}
                data-testid="fit-evo-cycle-badge"
              >
                Ciclo {cycle_number}
              </span>
            )}
          </h3>

          {/* Badge de nivel (Novato / Intermedio / Avanzado) */}
          {level?.label && (
            <div
              className="inline-block rounded-full px-3 py-1 mb-3"
              style={{ background: lvStyle.bg, border: `1px solid ${lvStyle.border}`, color: lvStyle.text }}
              data-testid="fit-evo-level-badge"
            >
              <p className="text-[11px] font-bold leading-tight">
                {level.message}
              </p>
            </div>
          )}

          {/* Barra de progreso */}
          <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden mb-3">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, var(--brand-primary), #00F0FF)' }}
            />
          </div>

          {/* Bloque actual */}
          {block && (
            <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-[10px] uppercase tracking-widest text-pink-300/90 font-bold flex items-center gap-1">
                <FaFire className="text-pink-400" /> Bloque {block.number}: {block.label}
              </p>
              <p className="text-xs text-white/85 mt-1 leading-snug">{block.focus}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-white/75">
                {block.adjustments?.rep_range && <span>🎯 Reps: {block.adjustments.rep_range}</span>}
                {block.adjustments?.rest_seconds && <span>⏱️ Descanso: {block.adjustments.rest_seconds}s</span>}
                {block.adjustments?.intensity && <span>💪 Intensidad: {block.adjustments.intensity}</span>}
              </div>
              {showBlockWelcome ? (
                (() => {
                  // Mensajes en primera persona (Deisy le habla a la alumna).
                  // Se muestran SOLO al iniciar un nuevo bloque (semanas 1, 5, 9).
                  const blockMessages = {
                    1: '🚀 ¡Bienvenida a tu fase inicial! He diseñado estas primeras 4 semanas para adaptar tu cuerpo y perfeccionar tu técnica base.',
                    2: '💪 ¡Subimos de nivel! He ajustado tu entrenamiento para aumentar la intensidad en esta nueva etapa.',
                    3: '🔥 ¡Fase final activada! Acabo de ajustar tu rutina para el reto definitivo de estas 12 semanas. ¡A por todas!',
                  };
                  const msg = blockMessages[block.number] || blockMessages[1];
                  return (
                    <div
                      className="mt-3 rounded-lg px-3 py-2.5"
                      style={{
                        background: 'linear-gradient(135deg, rgba(236,72,153,0.15), rgba(0,240,255,0.10))',
                        border: '1px solid rgba(236,72,153,0.35)',
                      }}
                      data-testid="coach-block-notification"
                    >
                      <p className="text-[11px] uppercase tracking-widest text-pink-300/90 font-bold mb-1">
                        💌 Mensaje de tu Coach
                      </p>
                      <p className="text-sm text-white/95 font-medium leading-snug">{msg}</p>
                    </div>
                  );
                })()
              ) : (
                // Semanas intermedias (2, 3, 4, 6, 7, 8, 10, 11, 12)
                <p
                  className="text-[12px] text-white/75 mt-3 italic leading-snug"
                  data-testid="coach-intermediate-message"
                >
                  ✨ Sigue avanzando en tu planificación estratégica de 12 semanas.
                </p>
              )}
            </div>
          )}

          <p className="text-[11px] text-white/60 mt-2">{message}</p>
        </div>
      </div>
    </motion.div>
  );
}
